import { FC, memo, useCallback, useRef, useState } from 'react';

import styled from '@mint-lib/styled';

import { IconButton, Popover } from '@myn/mui';

import { VERSION_ROW_TYPES } from '../../../../../../constants/versionsColumnsObject.js';
import {
  useBuyingPlanInfoSelector,
  useRefetchBuyingPlanInfo,
  useUpdateBuyingPlanInfoCache,
} from '../../../../../../hooks/useGetBuyingPlanData.js';
import { useGetVersionData } from '../../../../../../hooks/useGetVersionData.js';
import { useGetVersionListData } from '../../../../../../hooks/useGetVersionListData.js';
import { useSetIsArchivedVersion } from '../../../../../../hooks/useIsArchivedVersion.js';
import { useTableScale } from '../../../../../../hooks/useTableScale.js';
import {
  VersionsEventEmitterAction,
  VersionsTableRow,
} from '../../../../../../types/versions.js';
import eventEmitter from '../../../../../../utils/eventEmitter.js';
import CommentModal from '../../../../../Builder/BuilderHeader/CommentModal.jsx';
import ActionsFieldModal from './ActionsFieldModal.jsx';

type Props = {
  row: VersionsTableRow;
};

const VersionsActionsField: FC<Props> = memo(({ row }) => {
  const buttonRef = useRef(null);

  const { type, versionId } = row.original;
  const scale = useTableScale();
  const { version: selectedVersion } = useBuyingPlanInfoSelector() || {};

  const { data: { versions } = {} } = useGetVersionListData();
  const { refetch: getVersionData } = useGetVersionData({
    versionId,
  });

  const updateSelectedPlanCache = useUpdateBuyingPlanInfoCache();
  const refetchBuyingPlanInfo = useRefetchBuyingPlanInfo();
  const setIsArchivedVersion = useSetIsArchivedVersion();

  const isVersionType = type === VERSION_ROW_TYPES.VERSION;

  const [isOpen, setIsOpen] = useState(false);
  const [isCommentModalOpened, setIsCommentModalOpened] = useState(false);

  const versionDetails = versions?.find((version) => version.id === versionId);

  const handleOpenModal = useCallback(() => {
    setIsOpen(true);
    eventEmitter.emit(VersionsEventEmitterAction.OPEN_MODAL, row.id);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
    eventEmitter.emit(VersionsEventEmitterAction.CLOSE_MODAL, row.id);
  }, []);

  const handleOpenVersion = useCallback(async () => {
    eventEmitter.emit(VersionsEventEmitterAction.OPEN_VERSION, versionDetails);
    const { data } = await getVersionData();
    if (!data) {
      return;
    }

    if (data.isCurrent) {
      await refetchBuyingPlanInfo();
      setIsArchivedVersion(false);
      return;
    }

    setIsArchivedVersion(true);
    updateSelectedPlanCache(data.mediaPlanData);
  }, [versionDetails]);

  const handleDuplicateVersion = useCallback(() => {
    eventEmitter.emit(VersionsEventEmitterAction.DUPLICATE_VERSION, versionId);
  }, [row]);

  const handleOpenCommentModal = useCallback(() => {
    setIsCommentModalOpened(true);
  }, []);

  const handleCloseCommentModal = useCallback(() => {
    setIsCommentModalOpened(false);
  }, []);

  const handleVersionDelete = useCallback(() => {
    eventEmitter.emit(VersionsEventEmitterAction.DELETE_VERSION, versionId);
  }, [row]);

  return (
    <ActionsWrapper $scale={scale}>
      {isVersionType ? (
        <>
          <IconButton
            icon="OverflowMenuHorizontal"
            data-testid="open-action-menu-btn"
            onClick={handleOpenModal}
            variant="plain"
            ref={buttonRef}
          />

          <Popover
            open={isOpen}
            onClose={handleCloseModal}
            anchorEl={buttonRef.current}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <ActionsFieldModal
              onClickOpenVersion={handleOpenVersion}
              isOpeningDisabled={selectedVersion === versionDetails?.name}
              onClickDuplicateVersion={handleDuplicateVersion}
              canBeDuplicated={versionDetails?.canBeDuplicated}
              onClickAddNotes={handleOpenCommentModal}
              onClickDeleteVersion={handleVersionDelete}
              canBeDeleted={versionDetails?.canBeDeleted}
            />
          </Popover>
          <CommentModal
            id={versionId}
            description={versionDetails?.description}
            isOpened={isCommentModalOpened}
            onClose={handleCloseCommentModal}
          />
        </>
      ) : null}
    </ActionsWrapper>
  );
});

const ActionsWrapper = styled('div')<{ $scale: number }>`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  justify-content: center;
  border-left: none;
`;

export default VersionsActionsField;
